import { type ReactNode, type FC } from 'react'
import { useTranslation } from 'react-i18next'

import { AddressLine } from '@dhl/components'
import { Button, type ButtonProps, H6, Body1, useIsMobileVariant, Stack, Box, Accordion } from '@dhl-ui/components'

import { type Servicepoint } from './model.js'
import { ServicepointDistanceChip } from './ServicepointDistanceChip.js'
import { ServicepointOpeningTimes } from './ServicepointOpeningTimes.js'
import { ServicepointIcon } from './ServicepointIcon.js'

type Props = {
  editButtonText?: string
  onChangeButtonClick?: () => void
  servicepoint: Servicepoint
  showIcon?: boolean
  showOpeningTimes?: boolean
  title?: ReactNode
  servicePointTitle?: string
}

type PartialButtonProps = Pick<ButtonProps, 'children' | 'data-testid' | 'onClick'> & {
  'data-test'?: string
}

const EditButtonMobile: FC<PartialButtonProps> = (props) => (
  <Button color="dark" fullWidth variant="outlined" size="small" {...props} />
)

const EditButtonDesktop: FC<PartialButtonProps> = (props) => (
  <Button dense color="primary" sx={{ mt: 2 }} variant="text" {...props} />
)

export const ServicepointDetailsCard: FC<Props> = ({
  editButtonText: editButtonTextFromProps,
  onChangeButtonClick,
  servicepoint,
  showIcon,
  showOpeningTimes = true,
  title,
  servicePointTitle = 'DHL ServicePoint',
  ...rest
}) => {
  const isMobile = useIsMobileVariant()
  const { t } = useTranslation()

  const defaultEditButtonText =
    servicepoint.servicepointType === 'PackStation'
      ? 'servicepoint.servicepointDetailsCard.chooseDifferentLockerButton'
      : 'servicepoint.servicepointDetailsCard.chooseDifferentServicePointButton'
  const editButtonText = editButtonTextFromProps ?? defaultEditButtonText

  const ButtonComponent = isMobile ? EditButtonMobile : EditButtonDesktop

  return (
    <Stack alignItems="flex-start" gap={1.25} {...rest}>
      <Stack
        direction="row"
        alignItems="flex-start"
        justifyContent="space-between"
        spacing={2}
        width="100%"
        sx={{ pt: 2, px: 2 }}
      >
        <Stack>
          <Stack direction="row" alignItems="center" spacing={1}>
            {showIcon && (
              <ServicepointIcon
                component={Box}
                servicepointFormat={servicepoint.servicepointFormat}
                servicepointType={servicepoint.servicepointType}
              />
            )}
            {title ?? <H6>{servicepoint.name}</H6>}
            {title && <Body1>{servicepoint.name}</Body1>}
          </Stack>

          <AddressLine address={servicepoint.address} />
        </Stack>

        <Stack sx={{ flex: '0 0 auto' }}>
          <ServicepointDistanceChip distance={servicepoint.distance ?? 0} />
        </Stack>
      </Stack>

      {showOpeningTimes && (
        <Accordion
          title={t('servicepoint.servicepointDetailsDialog.openingTimesText')}
          background="dark"
          slotProps={{
            content: {
              noPaddingTop: true,
            },
          }}
        >
          <Box sx={{ lineHeight: 'normal' }}>
            <ServicepointOpeningTimes openingTimes={servicepoint.openingsTimes} />
          </Box>
        </Accordion>
      )}

      {onChangeButtonClick && (
        <Stack sx={{ pb: 2, px: 2, width: isMobile ? '100%' : 'unset' }}>
          <ButtonComponent
            data-test="intervention-edit-servicepoint-button"
            data-testid="intervention-edit-servicepoint-button"
            onClick={onChangeButtonClick}
          >
            {t(editButtonText, { servicePointTitle })}
          </ButtonComponent>
        </Stack>
      )}
    </Stack>
  )
}
